'use client';

import useEmblaCarousel from 'embla-carousel-react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Image from 'next/image';
import { useCallback, useState } from 'react';
import Container from '@/components/ui/container';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface ProjectItem {
	id: string; // Junction ID
	project_items_id: {
		id: string;
		title: string;
		description?: string;
		image: string;
	};
}

interface ProjectShowcaseProps {
	data: {
		headline?: string;
		background?: string;
		items: ProjectItem[];
	};
}

const ProjectShowcase = ({ data }: ProjectShowcaseProps) => {
	const { headline, background = 'white', items = [] } = data;
	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: 'start',
		// Optional: loop: true if explicitly requested later
	});

	const scrollPrev = useCallback(() => {
		if (emblaApi) emblaApi.scrollPrev();
	}, [emblaApi]);

	const scrollNext = useCallback(() => {
		if (emblaApi) emblaApi.scrollNext();
	}, [emblaApi]);

	// Background & Text Color Logic (Copied from MultiColumn for consistency)
	const getTheme = (bg: string) => {
		switch (bg) {
			case 'bright_snow':
				return { bg: 'bg-[#F9FAFB]', text: 'text-gray-900', heading: 'text-gray-900' };
			case 'carrot_orange':
				return { bg: 'bg-orange', text: 'text-white', heading: 'text-white' };
			case 'breuer_blue':
				return { bg: 'bg-[#003B64]', text: 'text-white', heading: 'text-white' };
			case 'seaweed':
				return { bg: 'bg-seaweed', text: 'text-white', heading: 'text-white' };
			case 'black':
				return { bg: 'bg-black', text: 'text-white', heading: 'text-white' };
			case 'white':
			default:
				return { bg: 'bg-white', text: 'text-gray-900', heading: 'text-gray-900' };
		}
	};

	const theme = getTheme(background);

	if (items.length === 0) return null;

	return (
		<section className={cn('py-16 w-full overflow-hidden', theme.bg)}>
			<Container>
				{/* Header with Nav Buttons (Desktop) */}
				<div className="flex items-center justify-between mb-8 md:mb-12">
					{headline && <h2 className={cn('text-3xl md:text-4xl font-heading', theme.heading)}>{headline}</h2>}

					{/* Desktop Navigation */}
					<div className="hidden md:flex items-center gap-2">
						<Button
							variant="outline"
							size="icon"
							onClick={scrollPrev}
							className="rounded-full border-gray-200 bg-white/10 hover:bg-white/20 hover:text-current backdrop-blur-sm transition-colors"
							aria-label="Previous project"
						>
							<ArrowLeft className={cn('size-5', theme.text)} />
						</Button>
						<Button
							variant="outline"
							size="icon"
							onClick={scrollNext}
							className="rounded-full border-gray-200 bg-white/10 hover:bg-white/20 hover:text-current backdrop-blur-sm transition-colors"
							aria-label="Next project"
						>
							<ArrowRight className={cn('size-5', theme.text)} />
						</Button>
					</div>
				</div>
			</Container>

			{/* Carousel Viewport - Full Width but constrained padding */}
			<div className="w-full relative">
				<div className="max-w-[1400px] mx-auto px-4 md:px-8 overflow-hidden" ref={emblaRef}>
					<div className="flex gap-4 md:gap-6 touch-pan-y">
						{items.map((item) => {
							const project = item.project_items_id;
							if (!project) return null; // Safety check

							return <ProjectCard key={item.id} project={project} theme={theme} />;
						})}
					</div>
				</div>
			</div>
		</section>
	);
};

interface ProjectCardProps {
	project: ProjectItem['project_items_id'];
	theme: { bg: string; text: string; heading: string };
}

const ProjectCard = ({ project, theme }: ProjectCardProps) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const isLongText = (project.description?.length || 0) > 120; // Approximate threshold for ~2 lines

	return (
		<div className="flex-[0_0_85%] md:flex-[0_0_45%] lg:flex-[0_0_30%] min-w-0">
			<div className="group relative flex flex-col h-full">
				{/* Image Card */}
				<div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-gray-100 mb-4">
					{project.image ? (
						<Image
							src={`${process.env.NEXT_PUBLIC_DIRECTUS_URL}/assets/${project.image}`}
							alt={project.title}
							fill
							className="object-cover transition-transform duration-500 group-hover:scale-105"
							sizes="(max-width: 768px) 85vw, (max-width: 1200px) 45vw, 30vw"
						/>
					) : (
						<div className="size-full flex items-center justify-center text-gray-400">No Image</div>
					)}
				</div>

				{/* Content */}
				<div className="flex flex-col flex-grow">
					<h3 className={cn('text-xl font-heading font-medium mb-2', theme.heading)}>{project.title}</h3>
					{project.description && (
						<div className="relative">
							<p
								className={cn(
									'text-base opacity-90 transition-all duration-300',
									theme.text,
									isExpanded ? '' : 'line-clamp-3', // Allow 3 lines before clamping
								)}
							>
								{project.description}
							</p>
							{isLongText && (
								<button
									onClick={() => setIsExpanded(!isExpanded)}
									className="mt-2 text-sm font-bold text-orange hover:text-orange-hover transition-colors focus:outline-none"
								>
									{isExpanded ? 'Lees minder' : 'Lees meer'}
								</button>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default ProjectShowcase;
